import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal, MaskedLine } from "@/components/site/Reveal";
import SectionHead from "@/components/site/SectionHead";
import CTASection from "@/components/site/CTASection";
import { useLang } from "@/i18n";

const ChiSono = () => {
  const { d } = useLang();
  const p = d.about;
  return (
    <div data-testid="chi-sono-page">
      <section className="bg-paper py-20 lg:py-28" data-testid="chi-sono-hero">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 sm:px-8 lg:grid-cols-12 lg:px-12">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-8 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-accent"
            >
              <span className="h-px w-10 bg-accent" /> {p.heroLabel}
            </motion.p>
            <h1 className="font-serif text-4xl font-light leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl" data-testid="chi-sono-headline">
              <MaskedLine delay={0.1}>{p.heroL1}</MaskedLine>
              <MaskedLine delay={0.25}>
                {p.heroL2a} <span className="italic text-accent">{p.heroL2b}</span>
              </MaskedLine>
            </h1>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="mt-8 max-w-2xl space-y-4 text-base leading-relaxed text-mutedink sm:text-lg"
            >
              <p>{p.p1}</p>
              <p>{p.p2}</p>
              <p>
                {p.p3a} <span className="text-ink">{p.p3b}</span>
                {p.p3c}
              </p>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.7 }} className="mt-10">
              <Link
                to="/contatti"
                data-testid="chi-sono-cta"
                className="inline-flex items-center gap-3 rounded-md bg-accent px-8 py-4 text-xs font-semibold uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:bg-accent-dark"
              >
                {p.cta} <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5"
          >
            <div className="relative">
              <img src="/images/cristina-desk.webp" alt={p.imgAlt} className="aspect-[4/5] w-full object-cover" data-testid="chi-sono-portrait" />
              <div className="absolute -bottom-5 -left-4 bg-ink px-6 py-4 sm:-left-6">
                <p className="font-serif text-xl text-paper">Cristina Angelini</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent">Marketing Facile</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="border-t border-ink/10 bg-white py-24 lg:py-32" data-testid="approach-section">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <SectionHead overline={p.approach.overline} title={p.approach.title} />
          <div className="mt-14 grid gap-px bg-ink/10 sm:grid-cols-2 lg:grid-cols-4">
            {p.approach.cards.map((a, i) => (
              <Reveal key={a.n} delay={i * 0.08} className="bg-white p-8 transition-colors duration-300 hover:bg-paper">
                <p className="font-serif text-5xl font-light text-outline">{a.n}</p>
                <h3 className="mt-5 font-serif text-xl font-normal text-ink">{a.t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-mutedink">{a.b}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="hero-grain relative bg-ink py-24 text-paper lg:py-32" data-testid="manifesto-section">
        <div className="relative mx-auto max-w-4xl px-6 text-center sm:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent-bright">{p.manifesto.overline}</p>
            <p className="mt-8 font-serif text-3xl font-light leading-snug sm:text-4xl lg:text-5xl">
              {p.manifesto.quoteA} <span className="italic text-accent-bright">{p.manifesto.quoteB}</span>
            </p>
            <p className="mx-auto mt-8 max-w-2xl text-base leading-relaxed text-white/60">{p.manifesto.body}</p>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </div>
  );
};

export default ChiSono;
